import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { REACH, reachIndex, reachOpensTo } from '../../lib/reach'
import ClipUpload from '../../components/ClipUpload'
import { Shell } from '../../components/Shell'
import ReachDial from '../../components/ReachDial'
import { Check, Spinner, ToggleRow } from '../../components/ui'

/**
 * Where a finished song goes.
 *
 * Deliberately NOT a step in the writing wizard, though the prototype had it as
 * step 5 of 5. Migration 17 separated finishing a song from sending it for a
 * reason: a writer with no church could otherwise never reach the end of the
 * flow. Welding sharing back onto the tail of the wizard would re-import that
 * bug in a new costume — and it would imply a song can only be shared once, at
 * the moment it is finished, which is the opposite of true.
 *
 * So it is its own screen, reachable from the finished song and from the song
 * list, and it can be changed as many times as the writer likes. The flow still
 * FEELS continuous because Done offers it as the obvious next thing.
 */
export default function Sharing() {
  const { id } = useParams()
  const nav = useNavigate()
  const { user, approved, isPlus } = useSession()

  const [song, setSong] = useState(null)
  // The writer's own hook for this song, if they have posted one.
  const [clip, setClip] = useState(null)
  const [circles, setCircles] = useState([])
  const [picked, setPicked] = useState([])
  const [innerCount, setInnerCount] = useState(0)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    const [{ data: s, error }, { data: cs }, { data: sc }, { data: cl }] = await Promise.all([
      supabase.from('songs').select('*').eq('id', id).maybeSingle(),
      supabase.from('circles')
        .select('*, circle_members(user_id)')
        .eq('owner_id', user.id)
        .order('kind')
        .order('created_at'),
      supabase.from('song_circles').select('circle_id').eq('song_id', id),
      supabase.from('song_clips').select('*')
        .eq('song_id', id).eq('owner_id', user.id).eq('kind', 'original')
        .is('removed_at', null).maybeSingle(),
    ])
    if (error) setErr(readableError(error))
    setSong(s ?? null)
    setCircles(cs ?? [])
    setPicked((sc ?? []).map((r) => r.circle_id))
    setClip(cl ?? null)
    setInnerCount((cs ?? []).find((c) => c.kind === 'inner')?.circle_members?.length ?? 0)
    setReady(true)
  }, [id, user.id])

  useEffect(() => { load() }, [load])

  if (!ready) return <Shell><Spinner /></Shell>
  if (!song || song.songwriter_id !== user.id) {
    return (
      <Shell back="/songs" title="Your songs">
        <h2 className="q">We can't find that song.</h2>
        <p className="sub">It may have been removed, or it isn't yours to open.</p>
      </Shell>
    )
  }

  const writersCircles = circles.filter((c) => c.kind === 'writers')
  const reach = song.reach
  const level = reachIndex(reach)

  // What each tier actually costs the writer in exposure, in real numbers.
  // "Church" is the people who can already see them, not a guess at attendance.
  const networkPeople = new Set(
    writersCircles.filter((c) => picked.includes(c.id))
      .flatMap((c) => (c.circle_members ?? []).map((m) => m.user_id))
  ).size
  const counts = {
    inner: innerCount ? `${innerCount} ${innerCount === 1 ? 'person' : 'people'}` : 'nobody yet',
    church: approved.length
      ? `${approved.length} ${approved.length === 1 ? 'church' : 'churches'}`
      : 'no church yet',
    network: picked.length
      ? `${networkPeople} across ${picked.length}`
      : 'no circles picked',
    collective: 'everyone',
  }

  const commit = async (nextReach, nextPicked = picked) => {
    setBusy(true); setErr(null)
    const { data, error } = await supabase.rpc('set_song_reach', {
      p_song: song.id,
      p_reach: nextReach,
      p_circles: nextPicked,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setSong(data ?? { ...song, reach: nextReach })
  }

  const gatedReach = (id) => !isPlus && reachIndex(id) > reachIndex('church')

  const setReach = (r) => {
    if (r === reach) return
    if (gatedReach(r)) { nav('/plans'); return }
    commit(r)
  }

  const togglePicked = (cid) => {
    const next = picked.includes(cid) ? picked.filter((x) => x !== cid) : [...picked, cid]
    setPicked(next)
    commit(reach, next)
  }

  /** The church this song is already with, for the "in review" state above. */
  const submittedTo = song.submitted_to_church_id
    ? approved.find((m) => m.church_id === song.submitted_to_church_id)?.churches
    : null

  /**
   * The one caller of submit_song(). Everything the RPC needs was already
   * true by this point — finished, declared, signed, splits at 100 — so the
   * only failure a writer can realistically hit here is not being approved at
   * the church, which is why the button is only rendered for churches that
   * have approved them.
   */
  const submitToChurch = async (churchId) => {
    setBusy(true); setErr(null)
    const { data, error } = await supabase.rpc('submit_song', {
      p_song: song.id, p_church: churchId,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setSong(data ?? song)
  }

  const setOption = async (key, value) => {
    if (value && !isPlus) { nav('/plans'); return }
    setSong({ ...song, [key]: value })
    const { error } = await supabase.from('songs').update({ [key]: value }).eq('id', song.id)
    if (error) { setErr(readableError(error)); load() }
  }

  const opened = reachOpensTo(reach)

  return (
    <Shell back={`/songs/${song.id}/done`} title={song.title || 'Untitled'} tabs>
      <div className="eyebrow">Sharing</div>
      <h2 className="q">You hold the keys.</h2>
      <p className="sub">
        Your song sits at the centre. Each ring out is a wider room — and every
        ring you open brings the ones inside it with it. Nothing moves until you
        move it, and you can pull it back whenever you like.
      </p>

      <ReachDial reach={reach} onChange={setReach} title={song.title} counts={counts} />

      <p className="dial-summary">
        {reach === 'private'
          ? <>This one is <b>only yours</b> so far.</>
          : <>Open to <b>{opened.join(' · ')}</b>.</>}
      </p>

      <div className="reachlist" role="group" aria-label="How far this song goes">
        {REACH.map((r, i) => {
          const on = i <= level
          const isEdge = i === level
          return (
            <button
              key={r.id}
              type="button"
              className={`reachrow${on ? ' on' : ''}${isEdge ? ' edge' : ''}`}
              aria-pressed={isEdge}
              disabled={busy}
              onClick={() => setReach(r.id)}
            >
              <span className={`reachtick${on ? '' : ' off'}`}>
                {on ? <Check size={11} w={3.2} /> : ''}
              </span>
              <span className="reachtext">
                <b>{r.label}</b>
                <small>{isEdge ? r.blurb : r.who}</small>
              </span>
              <span className="reachcount">
                {gatedReach(r.id) ? <span className="pill">Plus</span>
                  : r.id === 'private' ? '' : counts[r.id]}
              </span>
            </button>
          )
        })}
      </div>

      {/* The clip belongs here rather than on the upload step: it only means
          anything once a song reaches other writers, and it is a decision about
          reach, not about the recording. Shown from 'inner' up for the same
          reason the circle picker below is gated -- a private song has nobody
          to scroll past it. */}
      {level >= reachIndex('inner') && (
        <div className="reachsub">
          <ClipUpload song={song} clip={clip}
                      onChange={(row) => setClip(row)} />

          {/* The feed deliberately hides your own work from you, so without
              this a writer has no way to watch the clip they just posted. */}
          {clip && (
            <button className="btn-plain" style={{ marginTop: 10 }}
                    onClick={() => nav(`/collective/${song.id}`)}>
              Watch your hook ›
            </button>
          )}

          {/* The vault, from the screen the writer already opens to think
              about this song. The clip above is cut from whichever recording
              is the flagship, so "which take is the song" is a decision that
              belongs beside it. */}
          <div className="synclink">
            <button className="btn-plain" onClick={() => nav(`/songs/${song.id}/takes`)}>
              Every version of this song ›
            </button>
            <p className="small">
              Keep the voice memo and the Sunday recording. Say which one is the
              song, and that is what a church hears.
            </p>
          </div>

          {/* Only worth offering once there are words to hold in time. Without
              cues the feed still shows the lyric, paced by word count -- this
              replaces a decent guess with the truth, so it reads as an
              improvement to make, never a requirement to meet. */}
          <div className="synclink">
            <button className="btn-plain" onClick={() => nav(`/songs/${song.id}/sync`)}>
              {song.lyric_cues ? 'Re-sync the words' : 'Sync the words to the song'}
            </button>
            <p className="small">
              {song.lyric_cues
                ? 'The Collective holds each line in time with your recording.'
                : 'Tap along once and the Collective holds each line in time '
                  + 'instead of estimating it.'}
            </p>
          </div>
        </div>
      )}

      {/* Only asked when it's the tier actually in play. A circle picker sitting
          under a song that stops at "your church" is a question about nothing. */}
      {level >= reachIndex('network') && (
        <div className="reachsub">
          <div className="eyebrow" style={{ marginBottom: 10 }}>Which of your circles?</div>
          {writersCircles.length === 0 ? (
            <p className="small">
              You haven't made a writers' circle yet.{' '}
              <button className="linkish" onClick={() => nav('/circles')}>Start one</button> —
              until then this reaches nobody new.
            </p>
          ) : (
            <div className="chips">
              {writersCircles.map((c) => (
                <button key={c.id} className={`chip${picked.includes(c.id) ? ' on' : ''}`}
                        disabled={busy} onClick={() => togglePicked(c.id)}>
                  {c.name}
                  <span className="chipnum">{c.circle_members?.length ?? 0}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {level >= reachIndex('church') && (
        <>
          <div className="hr" />
          <div className="eyebrow">While it's out there</div>
          <ToggleRow
            title={isPlus ? 'Let others cover it' : 'Let others cover it · Plus'}
            hint={isPlus
              ? 'Another church can record their own version, crediting you'
              : 'Another church can record their own version, crediting you — upgrade to turn this on'}
            on={song.allow_covers}
            onChange={(v) => setOption('allow_covers', v)}
          />
          <ToggleRow
            title={isPlus ? "Open it to any pastor's feedback" : "Open it to any pastor's feedback · Plus"}
            hint={isPlus
              ? 'Not only the churches you belong to. Feedback, never a decision.'
              : 'Not only the churches you belong to — upgrade to turn this on'}
            on={song.open_feedback}
            onChange={(v) => setOption('open_feedback', v)}
          />
        </>
      )}

      {/* Reach is who may look; this is asking someone to decide. The two were
          always separate concepts (see lib/reach.js and the note on
          songs.destination in CLAUDE.md) but until now only reach had a
          control, so submit_song() had no caller anywhere in the app and no
          song could ever land in a reviewer's inbox. */}
      {song.finished_at && (
        <>
          <div className="hr" />
          <div className="eyebrow">Ask for a decision</div>
          {song.submitted_at ? (
            <div className="tile">
              <div className="row">
                <span style={{ fontWeight: 600 }}>With {submittedTo?.name || 'your church'}</span>
                <span className="badge">In review</span>
              </div>
              <p className="small" style={{ marginTop: 6 }}>
                Sent {new Date(song.submitted_at).toLocaleDateString()}
                {song.song_number ? ` · reference #${song.song_number}` : ''}. A reviewer
                will come back to you — you'll keep the song either way.
              </p>
            </div>
          ) : approved.length === 0 ? (
            <p className="small">
              Opening a song to a church lets them hear it. Asking for a decision needs a
              church that has approved you — <button className="linkish" onClick={() => nav('/join')}>
              add yours</button> and this turns on.
            </p>
          ) : (
            <>
              <p className="small" style={{ marginBottom: 10 }}>
                Letting a church see it and asking them to decide on it are different
                things. This puts it in a reviewer's queue and gives it a reference number.
              </p>
              {/* Stated plainly because it IS a grant of rights, small as it is.
                  A licence the writer only discovers afterwards isn't consent. */}
              <div className="tile" style={{ marginBottom: 10 }}>
                <p className="small" style={{ margin: 0 }}>
                  <b>This also lets them rehearse it.</b> A church can't judge a song
                  without playing it together, so sending it grants rehearsal permission
                  for three months — practice and run-throughs only. Not playing it in a
                  service, not recording it, not streaming it, not printing it. Those stay
                  yours to grant, or not, once they've responded. It expires on its own if
                  nothing comes of it.
                </p>
              </div>
              {approved.map((m) => (
                <button key={m.church_id} className="action col" disabled={busy}
                        onClick={() => submitToChurch(m.church_id)}>
                  <div className="row">
                    <span>Send to {m.churches?.name || 'your church'}</span>
                    <span className="ar">›</span>
                  </div>
                  <small>Review, plus rehearsal permission — you keep the copyright.</small>
                </button>
              ))}
            </>
          )}
        </>
      )}

      {/* A licence is a separate grant from reach or a review decision — see
          lib/licence.js. Reachable the moment there's a church to propose it
          to, same as it's always been; nothing here waits on a reviewer. */}
      {approved.length > 0 && (
        <>
          <div className="hr" />
          <div className="eyebrow">Copyright &amp; licence terms</div>
          <p className="small" style={{ marginBottom: 10 }}>
            Separately from a review decision, you can propose your church a licence —
            named permission to rehearse, play, record or stream this song, on terms you
            set. Your pastor checks it off against two plain tiers: Congregational Use and
            External Use.
          </p>
          <button className="action col" onClick={() => nav(`/songs/${song.id}/endorse/1`)}>
            <div className="row">
              <span>Propose licence terms</span>
              <span className="ar">›</span>
            </div>
            <small>You keep the copyright — this only sets what your church may do.</small>
          </button>
        </>
      )}

      {err && <p className="err">{err}</p>}

      <div className="hr" />
      <button className="btn btn-quiet" onClick={() => nav('/me')}>Back to your songs</button>
      <p className="small center" style={{ marginTop: 12 }}>
        Changing this later is one tap. A song that's gone out can always come back in.
      </p>
    </Shell>
  )
}
